/**
 * Safety Scan customer-facing presentation copy (display only).
 *
 * Safety Scan = a defined measurement window.
 * Narrative: Scope → Coverage → Observations → Review → Evidence → Result → Next step.
 * Optional secondary: Signed result.
 *
 * Observed-signals decision (traced, not a blind rename):
 * `observed_signals` are raw detection counts. Multiple signals may belong to the same
 * real-world event and are not 1:1 with a reviewable Observation (scan-membership episode).
 * Customer label is therefore **Measured signals**, not Observations.
 */

import {
  OBSERVATION_DISPOSITION_CONFIRM_TRUE_EVENT_LABEL,
  OBSERVATION_DISPOSITION_NEEDS_MORE_REVIEW_LABEL,
} from '../incidents/observationDispositionCopy';

export const SAFETY_SCAN_PRODUCT_NAME = 'Safety Scan';
export const DRAFT_SAFETY_SCAN_LABEL = 'Draft Safety Scan';
export const SIGNED_SAFETY_SCAN_LABEL = 'Signed Safety Scan';

export const MEASURED_SIGNALS_LABEL = 'Measured signals';
export const MEASURED_SIGNALS_EXPLANATION =
  'Measured signals are system detections in this scan window. They are not individual worker counts, not separate incidents or disciplinary events, and are not 1:1 with reviewable observations. Multiple signals may belong to the same real-world exposure event.';

export const RATE_WITHHELD_INSUFFICIENT_COVERAGE = 'Rate withheld · insufficient usable coverage';

export const SNAPSHOT_LABEL = 'Snapshot';
export const CLIP_LABEL = 'Clip';

export const SAFETY_SCAN_REVIEW_OUTCOMES = {
  confirmTrueEvent: OBSERVATION_DISPOSITION_CONFIRM_TRUE_EVENT_LABEL,
  falsePositive: 'False positive',
  duplicate: 'Duplicate',
  needsMoreReview: OBSERVATION_DISPOSITION_NEEDS_MORE_REVIEW_LABEL,
} as const;

export const SAFETY_SCAN_MEASUREMENT_WINDOW_COPY =
  'This Safety Scan summarizes verified evidence, the recorded Control, follow-up measurement, and the conclusion for this measurement window.';

export const SAFETY_SCAN_SIGNED_SECONDARY_COPY =
  'Signing freezes this scan record. Linked Safety Cases stay active and are not closed by scan sign-off.';

export const NO_MEASURED_SIGNALS_EMPTY =
  'No measured signals in this window. This is not a completed zero-event finding.';

export const MEASUREMENT_IN_PROGRESS_EMPTY =
  'Measurement is still in progress for this window.';

export const MEASURED_SIGNALS_COUNT_UNAVAILABLE =
  'Measured-signal count is not available in this package.';

export const REVIEW_REQUIRED_EPISODES_LABEL = 'Review required episodes';
export const VERIFY_OBSERVATIONS_LABEL = 'Verify observations';

export const LINKED_SAFETY_CASE_CONTEXT_TITLE = 'Linked Safety Case';

export function formatMeasuredSignalsCount(args: {
  count?: number | null;
  coverageInsufficient?: boolean;
  inProgress?: boolean;
}): string {
  if (args.coverageInsufficient) return RATE_WITHHELD_INSUFFICIENT_COVERAGE;
  if (args.inProgress) return MEASUREMENT_IN_PROGRESS_EMPTY;
  if (args.count == null || !Number.isFinite(Number(args.count))) {
    return MEASURED_SIGNALS_COUNT_UNAVAILABLE;
  }
  const count = Math.max(0, Math.floor(Number(args.count)));
  if (count === 0) return NO_MEASURED_SIGNALS_EMPTY;
  return String(count);
}

function formatScanCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.max(0, Math.floor(Number(value) || 0)));
}

export function formatMeasuredSignalsSentence(args: {
  count?: number | null;
  snapshots?: number | null;
  clips?: number | null;
  coverageInsufficient?: boolean;
  inProgress?: boolean;
}): string {
  const countLabel = formatMeasuredSignalsCount(args);
  if (
    countLabel === RATE_WITHHELD_INSUFFICIENT_COVERAGE
    || countLabel === MEASUREMENT_IN_PROGRESS_EMPTY
    || countLabel === NO_MEASURED_SIGNALS_EMPTY
    || countLabel === MEASURED_SIGNALS_COUNT_UNAVAILABLE
  ) {
    return `${countLabel} ${MEASURED_SIGNALS_EXPLANATION}`;
  }
  const formattedCount = formatScanCount(Number(args.count));
  const hasSnapshots = typeof args.snapshots === 'number';
  const hasClips = typeof args.clips === 'number';
  if (hasSnapshots && hasClips) {
    return `${formattedCount} measured signals were detected during the scan window, with ${formatScanCount(Number(args.snapshots))} snapshots and ${formatScanCount(Number(args.clips))} clips retained. ${MEASURED_SIGNALS_EXPLANATION}`;
  }
  return `${formattedCount} measured signals were detected during the scan window. ${MEASURED_SIGNALS_EXPLANATION}`;
}

export type SafetyScanHeaderModel = {
  overline: string;
  title: string;
  subtitle: string;
  secondaryCopy: string;
  stateLabel: string;
};

export function buildSafetyScanHeader(args: {
  patternLabel?: string | null;
  areaLabel?: string | null;
  timeWindowLabel?: string | null;
  signed?: boolean;
  operationalComplete?: boolean;
  reviewPending?: boolean;
  coverageInsufficient?: boolean;
  inProgress?: boolean;
}): SafetyScanHeaderModel {
  const pattern = String(args.patternLabel || '').trim();
  const area = String(args.areaLabel || '').trim();
  const window = String(args.timeWindowLabel || '').trim();
  const usablePattern = pattern
    && !/^risk pattern (loading|not selected)$/i.test(pattern)
    ? pattern
    : '';
  const usableArea = area
    && !/^area (not assigned|loading)$/i.test(area)
    ? area
    : '';
  const title = usablePattern && usableArea
    ? `${usablePattern} · ${usableArea}`
    : usablePattern || (args.signed ? SIGNED_SAFETY_SCAN_LABEL : DRAFT_SAFETY_SCAN_LABEL);

  let stateLabel = DRAFT_SAFETY_SCAN_LABEL;
  if (args.signed) stateLabel = SIGNED_SAFETY_SCAN_LABEL;
  else if (args.coverageInsufficient) stateLabel = RATE_WITHHELD_INSUFFICIENT_COVERAGE;
  else if (args.inProgress) stateLabel = 'In progress';
  else if (args.reviewPending) stateLabel = 'Needs review';
  else if (args.operationalComplete) stateLabel = 'Operationally complete';

  const subtitleParts = [window, stateLabel].filter(Boolean);
  return {
    overline: SAFETY_SCAN_PRODUCT_NAME,
    title,
    subtitle: subtitleParts.join(' · ') || stateLabel,
    secondaryCopy: args.signed
      ? `${SAFETY_SCAN_MEASUREMENT_WINDOW_COPY} ${SAFETY_SCAN_SIGNED_SECONDARY_COPY}`
      : SAFETY_SCAN_MEASUREMENT_WINDOW_COPY,
    stateLabel,
  };
}

export function buildSafetyScanPrimaryNextAction(args: {
  verifyPendingCount?: number | null;
  signed?: boolean;
  operationalComplete?: boolean;
  signedReady?: boolean;
  packageReady?: boolean;
}): { label: string; helper: string } {
  const pending = Math.max(0, Number(args.verifyPendingCount || 0));
  if (args.signed) {
    return {
      label: 'Review signed result',
      helper: 'This scan is already signed. Signed result is a frozen record, not the live Safety Case.',
    };
  }
  if (pending > 0) {
    return {
      label: pending === 1
        ? `${REVIEW_REQUIRED_EPISODES_LABEL} — 1 pending`
        : `${REVIEW_REQUIRED_EPISODES_LABEL} — ${pending} pending`,
      helper: 'Review the required episodes in this scan window. This is not a review of every image. This stays on scan membership and does not open the global observation queue.',
    };
  }
  if (args.signedReady) {
    return {
      label: 'Sign Safety Scan',
      helper: 'Required episodes are reviewed. Signing freezes this Safety Scan as a Signed Record revision.',
    };
  }
  if (args.packageReady && !args.signedReady) {
    return {
      label: 'Continue this Safety Scan',
      helper: 'Downloads are optional. Downloading does not sign.',
    };
  }
  if (args.operationalComplete) {
    return {
      label: 'Review scan result',
      helper: 'Workflow work is complete. Signed readiness is tracked separately.',
    };
  }
  return {
    label: 'Continue this Safety Scan',
    helper: 'Finish scope, coverage, observations, review, and evidence for this measurement window.',
  };
}

/** Display remapper for Safety Scan customer copy. Does not change stored fields. */
export function remapSafetyScanCustomerLabel(label: string): string {
  const raw = String(label || '');
  if (!raw) return raw;
  return raw
    .replace(/\bObserved-signal count\b/gi, 'Measured-signal count')
    .replace(/\bobserved-signal count\b/gi, 'measured-signal count')
    .replace(/\bobserved signals\b/gi, (match) => (match[0] === 'O' ? MEASURED_SIGNALS_LABEL : MEASURED_SIGNALS_LABEL.toLowerCase()))
    .replace(/\bObserved-signal\b/gi, 'Measured-signal')
    .replace(/\bstill images\b/gi, (match) => (match[0] === 'S' ? 'Snapshots' : 'snapshots'))
    .replace(/\bstill image\b/gi, (match) => (match[0] === 'S' ? 'Snapshot' : 'snapshot'))
    .replace(/\bObservation still\b/gi, 'Observation snapshot')
    .replace(/\bStill frame\b/gi, SNAPSHOT_LABEL);
}
